// Client-side mirrors of the backend accept/dismiss side effects
// (apply_ai_fields_to_security / DismissAIFieldView), used to patch the
// store in place instead of refetching the whole cap table. SCAL-8959.

type AnySecurity = Record<string, any>;

const refFieldName = (ref: any): string | undefined => ref?.field_name ?? ref?.fieldName;

// Soft-decline a reference list (SCAL-8976): flip is_accepted=false on every ref whose
// field name is in `fieldNames`, KEEPING the rest of the list intact so a later full
// fetch reconciles to the same reversible shape. Shape-agnostic (snake_case security
// refs and camelCase cell refs both resolve via refFieldName), so the in-grid cell
// dismiss and the security patch share ONE decline implementation.
export const declineRefsForFields = (refs: any[] | undefined, fieldNames: string[]): any[] | undefined =>
  Array.isArray(refs)
    ? refs.map(r => (fieldNames.includes(refFieldName(r) as string) ? { ...r, is_accepted: false } : r))
    : refs;

// SCAL-9152: the SINGLE source for the cap-table grid's "color by reference" GREEN rule.
// A cell is GREEN (.cell-with-references) iff it carries an Excel/cap-table MAPPING
// reference. PDF citation references do NOT turn a cell green - they only badge the value
// viewer (see conditions.tsx; MappingReference has no is_accepted, so presence alone is
// the rule). Accept/dismiss therefore never force green: the cell stays green only while a
// mapping reference survives, so green always reflects the cell's reference state, not the
// review action.
export const cellHasReferenceGreen = (
  cell: { mappingReferences?: unknown[] | null } | null | undefined
): boolean => (cell?.mappingReferences?.length ?? 0) > 0;

// Mirror of backend review_status_service.is_pending (SCAL-8976): a field is
// pending when present and not yet settled. status, when present, is
// authoritative ('pending' only); otherwise fall back to is_accepted (truthy =>
// settled). This keeps soft-dismissed fields (is_accepted=false, status=dismissed)
// out of the pending set.
const isPendingMeta = (meta: any): boolean => {
  if (meta == null) return false;
  if (meta.status) return meta.status === 'pending';
  return !meta.is_accepted;
};

export const pendingFieldNames = (security: AnySecurity): string[] => {
  const metadata = security?.ai_finding?.field_metadata ?? {};
  return Object.entries(metadata)
    .filter(([, meta]: [string, any]) => isPendingMeta(meta))
    .map(([fieldName]) => fieldName);
};

// Coercion contract: the AI metadata `value` is written onto the security field
// as-is (no type coercion, unlike the backend's cast_ai_field_value); the parser
// normalizes display and the next full fetch reconciles formatting.
export const applyAcceptToSecurity = (security: AnySecurity, fieldNames: string[]): AnySecurity => {
  const aiFinding = security?.ai_finding;
  if (!aiFinding?.field_metadata) return security;
  const targets = fieldNames.filter(f => isPendingMeta(aiFinding.field_metadata[f]));
  if (!targets.length) return security;

  const fieldMetadata = { ...aiFinding.field_metadata };
  const next: AnySecurity = { ...security, ai_finding: { ...aiFinding, field_metadata: fieldMetadata } };
  targets.forEach(f => {
    const meta = fieldMetadata[f];
    fieldMetadata[f] = { ...meta, status: 'accepted', is_accepted: true };
    if (meta.value !== undefined) next[f] = meta.value;
  });
  return next;
};

// SOFT decline (SCAL-8976): mirror the backend's reversible dismiss. The field
// metadata entry is KEPT (status='dismissed', is_accepted=false) instead of being
// removed, and matching citation refs are flipped is_accepted=false rather than
// dropped, so a later full fetch reconciles to the same soft shape.
export const applyDismissToSecurity = (security: AnySecurity, fieldNames: string[]): AnySecurity => {
  const aiFinding = security?.ai_finding;
  if (!aiFinding?.field_metadata) return security;
  const targets = fieldNames.filter(f => aiFinding.field_metadata[f] != null);
  if (!targets.length) return security;

  const fieldMetadata = { ...aiFinding.field_metadata };
  targets.forEach(f => {
    fieldMetadata[f] = { ...fieldMetadata[f], status: 'dismissed', is_accepted: false };
  });

  return {
    ...security,
    ai_finding: { ...aiFinding, field_metadata: fieldMetadata },
    citation_references: declineRefsForFields(security.citation_references, targets),
    mapping_references: declineRefsForFields(security.mapping_references, targets),
  };
};

// Accept the field VALUE while REJECTING its citation (SCAL-9162). Citations are
// created INDEPENDENTLY of the value finding and can be wrong, so an analyst may
// keep the AI value yet unlink its (bad) citation. This is the third review action
// alongside applyAcceptToSecurity (accept value + keep citation) and
// applyDismissToSecurity (reject value + reject citation): it composes the SAME two
// primitives instead of duplicating them - applyAcceptToSecurity applies the value
// and stamps the field accepted, then the shared declineRefsForFields soft-unlinks
// the field's citation refs (is_accepted=false, reversible - T8). The MAPPING refs
// are deliberately left intact, so the green color-by-reference rule follows mapping
// presence (cellHasReferenceGreen), never the rejected citation. Returns the input
// unchanged when accept applied nothing (no pending target).
export const acceptValueRejectCitation = (security: AnySecurity, fieldNames: string[]): AnySecurity => {
  const metadata = security?.ai_finding?.field_metadata;
  if (!metadata) {
    return security;
  }
  // Only the PENDING fields are actually accepted (mirrors applyAcceptToSecurity's
  // internal target filter), so decline ONLY those fields' citations - NOT a sibling
  // field passed in the same call that had no pending suggestion. Mirrors
  // applyDismissToSecurity, which passes the pre-filtered `targets` to declineRefsForFields.
  const targets = fieldNames.filter(f => isPendingMeta(metadata[f]));
  if (!targets.length) {
    return security;
  }
  const accepted = applyAcceptToSecurity(security, targets);
  return {
    ...accepted,
    citation_references: declineRefsForFields(accepted.citation_references, targets),
  };
};

export const removeCitationFromSecurities = (securities: AnySecurity[], citationId: number): AnySecurity[] =>
  securities.map(s => {
    const refs = s.citation_references;
    if (!Array.isArray(refs)) return s;
    const next = refs.filter((r: any) => r?.id !== citationId);
    return next.length === refs.length ? s : { ...s, citation_references: next };
  });
